import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_URLS, API_CONFIG } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ArrowLeft,
  Star,
  CheckCircle,
  Globe,
  Palette,
  Smartphone,
  ExternalLink,
  Image,
  Tag,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Eye,
  ShoppingCart,
  MessageSquare,
  Calendar,
  MessageCircle
} from 'lucide-react';
import ConsultationForm from '../ConsultationForm';
import ProjectQuestionForm from '../ProjectQuestionForm';
import MediaDisplay from '../MediaDisplay';
import { normalizeUrl } from '../../utils/format';

// Hover scroll effect styles
const customStyles = `
  .group:hover .group-hover\\:object-bottom {
    transition: all 35s ease;
  }
  .image-short { transition: all 5s ease !important; }
  .image-medium { transition: all 12s ease !important; }
  .image-long { transition: all 25s ease !important; }
  .image-very-long { transition: all 40s ease !important; }
`;

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

const CorporateDesignDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Get design by slug from URL
  const [design, setDesign] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contentReady, setContentReady] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [categories, setCategories] = useState([]);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Load company settings for WhatsApp contact
    const loadCompanySettings = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        const json = await res.json();
        if (json.status === 'success' && json.data) {
          setCompanySettings(json.data);
        }
      } catch (error) {
        console.error('Şirket ayarları yüklenemedi:', error);
      }
    };
    loadCompanySettings();

    // Load categories (only main categories for corporate designs)
    const loadCategories = async () => {
      try {
        const res = await fetch(API_URLS.CORPORATE_DESIGN_CATEGORIES);
        const json = await res.json();
        if (json.status === 'success' && Array.isArray(json.data)) {
          setCategories(json.data);
        } else if (Array.isArray(json)) {
          setCategories(json);
        }
      } catch (error) {
        console.error('Kategoriler yüklenemedi:', error);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const fetchDesignData = async () => {
      try {
        setLoading(true);
        
        if (!slug) {
          setError('Tasarım slug\'ı bulunamadı');
          setLoading(false);
          return;
        }
        
        const url = `${API_URLS.CORPORATE_DESIGN_DETAIL}&slug=${encodeURIComponent(slug)}`;
        const response = await fetch(url);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Tasarım bulunamadı veya artık mevcut değil');
          } else {
            setError('Tasarım verileri yüklenirken hata oluştu');
          }
          setLoading(false);
          return;
        }
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(`Non-JSON response: ${text.substring(0, 120)}...`);
        }
        const result = await response.json();
        
        if (result.status === 'error') {
          setError(result.message || 'Tasarım bulunamadı');
          setLoading(false);
          return;
        }
        
        const designData = result.data || result;
        setDesign({
          id: designData.id,
          name: designData.name,
          description: designData.description || '',
          long_description: designData.long_description || '',
          image_url: designData.image_url || 'https://placehold.co/800x600/e0e7ff/6366f1?text=Kurumsal+Web+Sitesi+Tasarımı',
          price: designData.price || '',
          demo_url: designData.demo_url || null,
          admin_url: designData.admin_url || null,
          purchase_url: designData.purchase_url || null,
          category_id: designData.category_id,
          category_ids: Array.isArray(designData.category_ids) ? designData.category_ids : (designData.category_id ? [designData.category_id] : []),
          category: designData.category || null,
          features: Array.isArray(designData.features) ? designData.features : [],
          tags: Array.isArray(designData.tags) ? designData.tags : [],
          gallery: designData.gallery || (designData.image_url ? [designData.image_url] : [])
        });
        
        // Set SEO meta tags from design data
        const metaTitle = designData.meta_title || designData.name;
        document.title = metaTitle;
        
        // Ensure meta tags exist and update them
        const ensureMeta = (selector, attrs) => {
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('meta');
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            document.head.appendChild(el);
          }
          return el;
        };
        
        // Description
        if (designData.meta_description) {
          const desc = ensureMeta('meta[name="description"]', { name: 'description' });
          desc.setAttribute('content', designData.meta_description);
        } else {
          const desc = document.querySelector('meta[name="description"]');
          if (desc) desc.remove();
        }
        
        // Keywords
        if (designData.meta_keywords) {
          const kw = ensureMeta('meta[name="keywords"]', { name: 'keywords' });
          kw.setAttribute('content', designData.meta_keywords);
        } else {
          const kw = document.querySelector('meta[name="keywords"]');
          if (kw) kw.remove();
        }
        
        // OG Title
        const ogTitle = ensureMeta('meta[property="og:title"]', { property: 'og:title' });
        ogTitle.setAttribute('content', metaTitle);
        
        // OG Description
        if (designData.meta_description) {
          const ogDesc = ensureMeta('meta[property="og:description"]', { property: 'og:description' });
          ogDesc.setAttribute('content', designData.meta_description);
        } else {
          const ogDesc = document.querySelector('meta[property="og:description"]');
          if (ogDesc) ogDesc.remove();
        }
        
        // OG Image
        const ogImageUrl = designData.og_image || designData.image_url;
        if (ogImageUrl) {
          const ogImage = ensureMeta('meta[property="og:image"]', { property: 'og:image' });
          const fullImageUrl = ogImageUrl.startsWith('http') ? ogImageUrl : `${API_CONFIG.BASE_URL}${ogImageUrl.startsWith('/') ? ogImageUrl : '/' + ogImageUrl}`;
          ogImage.setAttribute('content', fullImageUrl);
        } else {
          const ogImage = document.querySelector('meta[property="og:image"]');
          if (ogImage) ogImage.remove();
        }
        
        if (designData.category) {
          setCategory(designData.category);
        } else if (designData.category_id) {
          // Fetch category separately if not included
          try {
            const catResponse = await fetch(`${API_URLS.CORPORATE_DESIGN_CATEGORIES}`);
            const catData = await catResponse.json();
            const categories = catData.status === 'success' ? catData.data : catData;
            const foundCategory = Array.isArray(categories) ? categories.find(c => c.id === designData.category_id) : null;
            if (foundCategory) {
              setCategory(foundCategory);
            }
          } catch (e) {
            console.error('Error fetching category:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching design:', error);
        if (!error.message || error.message.includes('HTTP 404')) {
          setError('Tasarım bulunamadı veya artık mevcut değil');
        } else {
          setError('Tasarım verileri yüklenirken hata oluştu');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDesignData();
  }, [slug]);

  const handlePrevImage = () => {
    if (design?.gallery && design.gallery.length > 0) {
      setLightboxIndex((prev) => (prev === 0 ? design.gallery.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (design?.gallery && design.gallery.length > 0) {
      setLightboxIndex((prev) => (prev === design.gallery.length - 1 ? 0 : prev + 1));
    }
  };

  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      }
      if (e.key === 'ArrowLeft') {
        if (design?.gallery && design.gallery.length > 0) {
          setLightboxIndex((prev) => (prev === 0 ? design.gallery.length - 1 : prev - 1));
        }
      }
      if (e.key === 'ArrowRight') {
        if (design?.gallery && design.gallery.length > 0) {
          setLightboxIndex((prev) => (prev === design.gallery.length - 1 ? 0 : prev + 1));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, design]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Tasarım yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !design) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Tasarım Bulunamadı'}
          </h1>
          <Link 
            to="/web-sitesi-tasarimlari" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Web Sitesi Tasarımları sayfasına dön
          </Link>
        </div>
      </div>
    );
  }

  // Capitalize category name helper
  const capitalizeCategoryName = (name) => {
    if (!name) return name;
    return name
      .split(' ')
      .map(word => {
        if (!word) return '';
        const firstChar = word.charAt(0);
        if (['i', 'ı', 'u', 'ü', 'o', 'ö', 'e', 'a'].includes(firstChar.toLowerCase())) {
          return firstChar.toLocaleUpperCase('tr-TR') + word.slice(1);
        }
        return firstChar.toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Link 
              to="/web-sitesi-tasarimlari" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Web Sitesi Tasarımları
            </Link>
            <ChevronRight className="w-4 h-4 text-white/60" />
            <span className="text-white font-medium">{design.name}</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-lg px-5 py-2.5 rounded-full text-base font-medium">
                  <Palette className="w-5 h-5" />
                  <span>{category?.name ? capitalizeCategoryName(category.name) : 'Kurumsal Web Sitesi Tasarımı'}</span>
                  <Badge className="bg-blue-400 text-blue-900">Tasarım</Badge>
                </div>
                
                <h1 className="text-3xl md:text-5xl font-bold leading-tight">
                  {design.name}
                </h1>
                
                <p className="text-lg text-blue-100 leading-relaxed">
                  {design.description}
                </p>
              </div>

              {/* Price */}
              {design.price && (
                <div className="text-center sm:text-left">
                  <p className="text-2xl font-bold">₺ {design.price}</p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                {design.demo_url && (
                  <Button 
                    onClick={() => {
                      const normalizedUrl = normalizeUrl(design.demo_url);
                      window.open(normalizedUrl, '_blank');
                    }}
                    className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-2.5 text-base"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Demo'yu Görüntüle
                  </Button>
                )}
                {design.admin_url && (
                  <Button 
                    onClick={() => {
                      const normalizedUrl = normalizeUrl(design.admin_url);
                      window.open(normalizedUrl, '_blank');
                    }}
                    className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-2.5 text-base"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                )}
                {design.purchase_url && (
                  <Button 
                    onClick={() => window.open(design.purchase_url, '_blank')}
                    className="bg-green-600 text-white hover:bg-green-700 px-6 py-2.5 text-base"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Satın Al
                  </Button>
                )}
                <Button 
                  onClick={() => setShowQuestionForm(true)} 
                  className="border-white text-white hover:bg-white hover:text-blue-600 px-6 py-2.5 text-base"
                  variant="outline"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Tasarım Hakkında Soru Sor
                </Button>
                {companySettings && (companySettings.phone || companySettings.whatsapp) && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 text-base"
                    onClick={() => {
                      const phoneNumber = (companySettings.whatsapp || companySettings.phone || '').replace(/[^\d]/g, '');
                      if (phoneNumber) {
                        const message = encodeURIComponent(`Merhaba, ${design.name} tasarımı hakkında bilgi almak istiyorum.`);
                        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                      }
                    }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp ile İletişim
                  </Button>
                )}
              </div>
            </div>
            
            <div className="relative group">
              <div className="aspect-[4/5] bg-white/10 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/20">
                <MediaDisplay
                  src={design.image_url}
                  alt={design.name}
                  className="w-full h-full object-cover object-top transition-all duration-[35s] ease group-hover:object-bottom"
                  videoProps={{ controls: true }}
                  imgProps={{
                    onLoad: (e) => {
                      const nh = e.target.naturalHeight;
                      const cls = getImageTransitionClass(nh);
                      if (cls) e.target.classList.add(cls);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            {/* Desktop Tabs */}
            <nav className="hidden md:flex -mb-px space-x-8">
              {[
                { id: 'overview', label: 'Genel Bakış', icon: Eye },
                { id: 'features', label: 'Özellikler', icon: CheckCircle },
                { id: 'gallery', label: 'Galeri', icon: Image }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
            
            {/* Mobile Tabs */}
            <nav className="md:hidden -mb-px">
              <div className="flex flex-wrap gap-2 pb-2">
                {[
                  { id: 'overview', label: 'Genel Bakış', shortLabel: 'Genel', icon: Eye },
                  { id: 'features', label: 'Özellikler', shortLabel: 'Özellikler', icon: CheckCircle },
                  { id: 'gallery', label: 'Galeri', shortLabel: 'Galeri', icon: Image }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 py-2 px-3 border-b-2 font-medium text-xs ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50 rounded-t-lg'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{tab.shortLabel}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>

        {/* Tab Content with Shared Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Tasarım Hakkında</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">
                      {design.long_description || design.description}
                    </p>
                  </CardContent>
                </Card>

                {/* Tags */}
                {design.tags && design.tags.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Etiketler</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {design.tags.map((tag, index) => (
                          <Badge key={index} variant="secondary" className="px-3 py-1">
                            <Tag className="w-3 h-3 mr-1" />
                            {typeof tag === 'object' ? tag.name || tag : tag}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Categories - Only main categories (no tree) */}
                {categories.length > 0 && (() => {
                  const designCategoryIds = (design.category_ids || (design.category_id ? [design.category_id] : [])).map(id => String(id));
                  
                  if (designCategoryIds.length === 0) {
                    return null;
                  }
                  
                  // Filter only main categories (parent_id is NULL)
                  const mainCategories = categories.filter(cat => !cat.parent_id);
                  const relevantCategories = mainCategories.filter(cat => designCategoryIds.includes(String(cat.id)));
                  
                  if (relevantCategories.length === 0) {
                    return null;
                  }
                  
                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle>Kategoriler</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {relevantCategories.map(cat => (
                            <Link
                              key={cat.id}
                              to={`/web-sitesi-tasarimlari?category=${cat.slug || cat.id}`}
                              className="px-3 py-2 rounded-lg bg-purple-100 text-purple-700 font-medium hover:bg-purple-200 transition-colors text-sm"
                            >
                              {capitalizeCategoryName(cat.name)}
                            </Link>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </div>
            )}

            {activeTab === 'features' && (
              <Card>
                <CardHeader>
                  <CardTitle>Tasarım Özellikleri</CardTitle>
                </CardHeader>
                <CardContent>
                  {design.features && design.features.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {design.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="font-medium text-gray-900">
                            {typeof feature === 'object' ? feature.name || feature.title || JSON.stringify(feature) : feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">Henüz özellik eklenmemiş</p>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'gallery' && (
              <Card>
                <CardHeader>
                  <CardTitle>Tasarım Galerisi</CardTitle>
                </CardHeader>
                <CardContent>
                  {design.gallery && design.gallery.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {design.gallery.map((image, index) => (
                        <div 
                          key={index} 
                          className="aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => {
                            setLightboxIndex(index);
                            setLightboxOpen(true);
                          }}
                        >
                          <MediaDisplay
                            src={image}
                            alt={`${design.name} - Görsel ${index + 1}`}
                            className="w-full h-full object-cover"
                            videoProps={{ controls: true }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                      <MediaDisplay
                        src={design.image_url}
                        alt={design.name}
                        className="w-full h-full object-cover"
                        videoProps={{ controls: true }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Shared Sidebar - Same for all tabs */}
          <div className="space-y-6">
            {/* Design Info */}
            <Card>
              <CardHeader>
                <CardTitle>Tasarım Bilgileri</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {design.price && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fiyat:</span>
                    <span className="font-medium text-blue-600">₺ {design.price}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              {design.demo_url && (
                <Button 
                  className="w-full" 
                  onClick={() => window.open(design.demo_url, '_blank')}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Demo'yu Görüntüle
                </Button>
              )}
              {design.admin_url && (
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => window.open(design.admin_url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              {design.purchase_url && (
                <Button 
                  className="w-full" 
                  onClick={() => window.open(design.purchase_url, '_blank')}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Satın Al
                </Button>
              )}
              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => setShowQuestionForm(true)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Tasarım Hakkında Soru Sor
              </Button>
              {companySettings && (companySettings.phone || companySettings.whatsapp) && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => {
                    const phoneNumber = (companySettings.whatsapp || companySettings.phone || '').replace(/[^\d]/g, '');
                    if (phoneNumber) {
                      const message = encodeURIComponent(`Merhaba, ${design.name} tasarımı hakkında bilgi almak istiyorum.`);
                      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                    }
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp ile İletişim
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            Bu Tasarımı İstiyor musunuz?
          </h2>
          <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
            Uzman ekibimizle birlikte {design.name} tasarımını sizin için özelleştirelim.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowConsultationForm(true)} className="bg-white text-purple-600 hover:bg-gray-100">
              <MessageSquare className="w-5 h-5 mr-2" />
              Ücretli Danışmanlık
            </Button>
            <Button onClick={() => { navigate('/iletisim'); }} variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600">
              <Calendar className="w-5 h-5 mr-2" />
              İletişime Geç
            </Button>
          </div>
        </div>
      </div>
      
      {/* Lightbox Modal */}
      {lightboxOpen && design.gallery && design.gallery.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 z-[110] p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 z-[110] px-4 py-2 bg-white/10 rounded-full text-white">
            {lightboxIndex + 1} / {design.gallery.length}
          </div>

          {/* Previous Button */}
          {design.gallery.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrevImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Next Button */}
          {design.gallery.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-[110] p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          )}

          {/* Image */}
          <div 
            className="relative mx-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <MediaDisplay
              src={design.gallery[lightboxIndex]}
              alt={`${design.name} - Görsel ${lightboxIndex + 1}`}
              className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg"
              videoProps={{ controls: true }}
            />
          </div>
        </div>
      )}

      {showQuestionForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="relative z-50 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <ProjectQuestionForm project={design} onClose={() => setShowQuestionForm(false)} />
          </div>
        </div>
      )}

      {showConsultationForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative z-50 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <ConsultationForm isModal={true} onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CorporateDesignDetailPage;
